import React from "react";
import {
  Box,
  Icon,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  Separator,
  Text,
} from "@salestrak/ui";

import { SettingsGroupRow } from "#app/components/settings/settings-group";
import {
  THEME_MODE_OPTIONS,
  ThemeAccentSelect,
  ThemeResetButton,
  ThemeSurfaceSelect,
  ThemeContrastSelect,
  useIsAppearanceCustomized,
  useThemeModePreference,
} from "#app/components/theme/theme-appearance-controls";

// ---------------------------------------------------------------------------
// Shared theme picker content (used by both sub-menu and preferences)
// ---------------------------------------------------------------------------

const THEME_TITLE = "Theme";
const THEME_DESCRIPTION = "Choose how your workspace looks.";

// One width for all three controls. It is a floor, not a preference: the Select
// trigger carries `min-w-36`, so anything narrower overflows the row instead of
// shrinking. The popup width below is then whatever leaves the label column
// room beside it.
const CONTROL_WIDTH = 144;
const POPUP_WIDTH = 340;

export function ThemePickerContent({
  showHeader,
}: { showHeader?: boolean } = {}) {
  const { mode, setMode } = useThemeModePreference();
  const isCustomized = useIsAppearanceCustomized();

  return (
    <Box direction="column" gap="lg">
      {showHeader && (
        <Box direction="column" gap="xs" paddingX="sm">
          <Text size="base" weight="semibold">
            {THEME_TITLE}
          </Text>
          <Text size="xs" variant="muted">
            {THEME_DESCRIPTION}
          </Text>
        </Box>
      )}

      <Box direction="column" gap="xs">
        <Box paddingX="sm">
          <Text size="xs" weight="medium" variant="muted">
            Appearance
          </Text>
        </Box>
        <Box direction="row" gap="xs">
          {THEME_MODE_OPTIONS.map((opt) => {
            const isSelected = mode === opt.value;
            return (
              <Box
                key={opt.value}
                as="button"
                direction="column"
                align="center"
                gap="xs"
                flex={1}
                padding="sm"
                rounded="md"
                border
                borderColor={isSelected ? "primary" : undefined}
                hoverBackground
                cursor="pointer"
                onClick={() => setMode(opt.value)}
                aria-label={`${opt.label} appearance`}
                aria-pressed={isSelected}
              >
                <Icon name={opt.icon} size="md" color="muted" />
                <Text size="xs" weight={isSelected ? "semibold" : "normal"}>
                  {opt.label}
                </Text>
              </Box>
            );
          })}
        </Box>
      </Box>

      {/* A plain rounded Box, not a Card: Card adds the inner highlight ring,
          which reads as a bright seam along the top edge in dark mode. This is
          a well inside a popup, so `sunken` — nothing sits above `popover`. */}
      <Box
        surface="sunken"
        direction="column"
        rounded="lg"
        border
        overflow="hidden"
      >
        <SettingsGroupRow label="Background" subtitle="Lightness">
          <ThemeSurfaceSelect width={CONTROL_WIDTH} />
        </SettingsGroupRow>

        <Box paddingX="md">
          <Separator />
        </Box>

        <SettingsGroupRow label="Text" subtitle="Contrast">
          <ThemeContrastSelect width={CONTROL_WIDTH} />
        </SettingsGroupRow>

        <Box paddingX="md">
          <Separator />
        </Box>

        <SettingsGroupRow label="Accent" subtitle="Primary color">
          <ThemeAccentSelect width={CONTROL_WIDTH} />
        </SettingsGroupRow>
      </Box>

      {isCustomized ? (
        <Box direction="row" justify="end">
          <ThemeResetButton />
        </Box>
      ) : null}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ThemeSubMenu — hover sub-menu hosting the picker
// ---------------------------------------------------------------------------

export function ThemeSubMenu() {
  return (
    <MenuSub>
      <MenuSubTrigger>
        <Icon name="sun-moon" size="md" color="muted" />
        <Text>Theme</Text>
      </MenuSubTrigger>
      <MenuSubPopup>
        <Box paddingY="sm" paddingX="sm" width={POPUP_WIDTH}>
          <ThemePickerContent showHeader />
        </Box>
      </MenuSubPopup>
    </MenuSub>
  );
}
